import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch } from './session';

export interface AuthUser {
  sub: string;
  role: 'platform_admin' | 'tenant_user' | 'end_user';
  storeId: number | null;
  email?: string;
  displayName?: string | null;
}

export interface EntitlementsSnapshot {
  storeId: number;
  plan_code: string | null;
  modules: string[];
  enforcement_mode: 'off' | 'observe' | 'enforce';
  category?: string | null;
}

interface AuthContextValue {
  ready: boolean;
  user: AuthUser | null;
  entitlements: EntitlementsSnapshot | null;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  refreshEntitlements: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string; code?: string };
    return body.error || body.message || body.code || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [entitlements, setEntitlements] = useState<EntitlementsSnapshot | null>(null);

  const refreshEntitlements = useCallback(async () => {
    const res = await apiFetch('/api/v1/tenant/entitlements');
    if (res.status === 401) {
      setUser(null);
      setEntitlements(null);
      return;
    }
    if (!res.ok) {
      // platform_admin may lack store scope — keep session, empty modules
      if (res.status === 403) {
        setEntitlements(null);
        return;
      }
      throw new Error(await readError(res));
    }
    const body = (await res.json().catch(() => null)) as EntitlementsSnapshot | null;
    if (!body) {
      setEntitlements(null);
      return;
    }
    setEntitlements({
      storeId: Number(body.storeId),
      plan_code: body.plan_code ?? null,
      modules: Array.isArray(body.modules) ? body.modules.map((m) => String(m).toLowerCase()) : [],
      enforcement_mode: body.enforcement_mode || 'enforce',
      category: body.category ?? null,
    });
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/auth/session');
      if (!res.ok) {
        setUser(null);
        setEntitlements(null);
        setReady(true);
        return;
      }
      const body = (await res.json().catch(() => null)) as { auth: AuthUser } | null;
      if (!body?.auth) {
        setUser(null);
        setEntitlements(null);
        setReady(true);
        return;
      }
      setUser(body.auth);
      if (body.auth.role === 'tenant_user' && body.auth.storeId) {
        await refreshEntitlements();
      } else {
        setEntitlements(null);
      }
    } catch {
      setUser(null);
      setEntitlements(null);
    } finally {
      setReady(true);
    }
  }, [refreshEntitlements]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const login = useCallback(
    async (identifier: string, password: string) => {
      const res = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
      });
      if (!res.ok) throw new Error(await readError(res));
      const body = (await res.json().catch(() => null)) as {
        auth: AuthUser & { email?: string; displayName?: string | null };
      } | null;
      if (!body?.auth) throw new Error('Risposta login non valida');
      setUser(body.auth);
      if (body.auth.role === 'tenant_user' && body.auth.storeId) {
        await refreshEntitlements();
      } else {
        setEntitlements(null);
      }
    },
    [refreshEntitlements],
  );

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/v1/auth/logout', { method: 'POST' });
    } catch {
      /* ignore network errors during logout */
    }
    setUser(null);
    setEntitlements(null);
  }, []);

  const value = useMemo(
    () => ({ ready, user, entitlements, login, logout, refreshEntitlements }),
    [ready, user, entitlements, login, logout, refreshEntitlements],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth requires AuthProvider');
  return ctx;
}
